<template>
  <div class="chat">
    <v-row no-gutters justify="start" class="fill-height">
      <v-col
        xs="12"
        sm="4"
        lg="3"
        class="chat-c1 fill-height"
        v-show="!($vuetify.breakpoint.xsOnly && currentChannelSid)"
      >
        <div class="fill-height full-width d-inline-flex flex-column">
          <div class="chat-c1-head d-flex flex-column flex-shrink-0">
            <div class="d-flex justify-space-between align-center">
              <div>{{ $t("chat.title") }}</div>
              <v-progress-circular v-if="generalLoading" indeterminate color="primary" size="20" width="2" />
            </div>
            <v-btn depressed color="primary" class="mt-4" @click.stop="action('startConversation')">
              {{ $t("chat.startConversation") }}
            </v-btn>
          </div>
          <div class="chat-c1-list flex-grow-1">
            <ChannelsList
              :loading="generalLoading"
              :channelsList="activeChannelsList"
              :currentChannelSid="currentChannelSid"
              @selectChannel="selectChannel"
              v-show="activeGroup === currentGroup"
            />
            <ChannelsList
              :loading="generalLoading"
              :channelsList="archiveChannelsList"
              :currentChannelSid="currentChannelSid"
              @selectChannel="selectChannel"
              v-show="archiveGroup === currentGroup"
            />
          </div>
          <div class="chat-c1-bottom d-flex flex-shrink-0">
            <SwitchItem
              icon="$vuetify.icons.chatMessages"
              icon-size="20"
              :text="$t('chat.activeChats')"
              :active="activeGroup === currentGroup"
              @click="selectGroup(activeGroup)"
            />
            <SwitchItem
              icon="$vuetify.icons.chatArchive"
              icon-size="18"
              :text="$t('chat.archive')"
              :active="archiveGroup === currentGroup"
              @click="selectGroup(archiveGroup)"
            />
          </div>
        </div>
      </v-col>
      <v-col
        xs="12"
        sm="8"
        lg="9"
        class="chat-c2 fill-height"
        v-show="!($vuetify.breakpoint.xsOnly && !currentChannelSid)"
      >
        <Preview v-if="!currentChannelSid" :text="$t('chat.preview.noChatSelected')" />
        <Channel
          v-for="channel in activeChannels"
          :key="channel.sid"
          :channel="channel"
          v-show="channel.sid === currentChannelSid"
          :active="channel.sid === currentChannelSid"
          @back="currentChannelSid = null"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { Client } from "twilio-chat";
import Vue from "vue";
import { mapActions, mapGetters, mapMutations } from "vuex";

import Channel from "./Channel.vue";
import ChannelsList from "./ChannelsList/List.vue";
import Preview from "./Preview.vue";
import SwitchItem from "./SwitchItem.vue";

export default Vue.extend({
  name: "Chat",
  components: {
    Channel,
    ChannelsList,
    SwitchItem,
    Preview,
  },
  data: () => ({
    message: "",
    item: null,
    client: null,
    header: "Chat",
    channelsList: [],
    archiveChannelsList: [],
    activeChannelsList: [],
    activeChannels: [],
    currentChannelSid: null,
    generalLoading: false,
    currentGroup: "active",
    activeGroup: "active",
    archiveGroup: "archive",
  }),
  methods: {
    ...mapActions("chat", ["connect"]),
    ...mapMutations("chat", ["setSelectedChannel", "setUnreadChannels"]),

    async selectChannel(sid) {
      if (!this.channelsList || this.channelsList.length === 0) {
        return;
      }
      const channel = this.channelsList.find((item) => item.sid === sid);
      if (!channel) {
        this.currentChannelSid = null;
        return;
      }
      const channelIndex = this.activeChannels.findIndex((item) => item.sid === sid);
      if (channelIndex !== -1) {
        this.activeChannels[channelIndex] = channel;
      } else {
        this.activeChannels.push(channel);
      }
      this.setSelectedChannel(sid);
      this.currentChannelSid = sid;
      this.setUnreadChannels({ [sid]: 0 });
    },
    async init() {
      this.generalLoading = true;
      await this.connect();
      this.client = await Client.create(this.token);
      this.client.on("channelAdded", this.addChannel);
      this.client.on("channelRemoved", this.loadChannelList);
      this.client.on("tokenExpired", this.init);
      this.client.on("connectionError", this.init);
      this.client.on("tokenAboutToExpire", this.init);
      setTimeout(async () => {
        if (!this.channelsList.length) {
          await this.loadChannelList();
        }
        this.generalLoading = false;
      }, 1000);
    },
    async loadChannelList() {
      const channels = await this.client.getUserChannelDescriptors();
      await Promise.all(
        channels.items.map(async (channelDescriptor) => {
          const channel = await channelDescriptor.getChannel();
          this.addChannel(channel);
        }),
      );
    },
    addChannel(channel) {
      let foundIndex = this.channelsList.findIndex((item) => item.sid === channel.sid);
      if (foundIndex !== -1) {
        this.channelsList[foundIndex] = channel;
      } else {
        this.channelsList.push(channel);
        channel.on("updated", this.updateChannelList);
      }
      this.updateChannelList();
    },
    updateChannelList() {
      this.channelsList.sort((a, b) => {
        const aDate = new Date(a.lastMessage?.dateCreated || a.dateCreated);
        const bDate = new Date(b.lastMessage?.dateCreated || b.dateCreated);
        return bDate - aDate;
      });
      this.activeChannelsList = [...this.channelsList].filter((channel) => !channel.attributes[this.archiveGroup]);
      this.archiveChannelsList = [...this.channelsList].filter((channel) => channel.attributes[this.archiveGroup]);
    },
    selectGroup(val) {
      this.currentGroup = val;
      this.updateChannelList();
    },
    action(val) {
      this.$emit("action", val);
    },
  },
  computed: {
    ...mapGetters("chat", ["token"]),
  },
  async mounted() {
    await this.init();
  },
  deactivated() {
    this.currentChannelSid = null;
  },
  watch: {
    async item() {
      await this.selectChannel(this.item);
    },
  },
});
</script>

<style scoped lang="scss">
.chat {
  padding: 0;
  background: #ffffff;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.08);
  border-radius: 3px;
  overflow: hidden;
  &-c1 {
    border-right: 1px solid #efeff0;
    &-head {
      font-weight: 500;
      font-size: 22px;
      color: $primaryblack2;
      padding: 25px 24px;
      border-bottom: 1px solid #efeff0;
    }
    &-list {
      overflow-y: scroll;
      overflow-x: hidden;
    }
    &-bottom {
      height: 62px;
      border-top: 1px solid #efeff0;
    }
  }
}
.preview-text {
  font-weight: 500;
  font-size: 12px;
  line-height: 15px;
  color: $primaryblack2;
  opacity: 0.75;
}
</style>
